/**
 * Message catalogues.
 *
 * English is the source of truth for the *shape*: `Messages` is derived from
 * it, so a key added here and not translated is a **compile error** in
 * `am.ts`, not a blank label discovered by a player. That is the entire reason
 * this is a typed object rather than a JSON file behind a `t(string)` lookup —
 * an untyped catalogue silently degrades, and the degradation is invisible to
 * everyone who reads the language you happen to speak.
 *
 * No i18n dependency: two languages, no pluralisation rules worth importing a
 * library for, and interpolation is a function parameter.
 */

export const en = {
  appName: 'Bingo',

  // Connection and access
  openInTelegram: 'Open this app from Telegram',
  openInTelegramHint: 'Bingo runs inside Telegram so it can verify who you are.',
  connecting: 'Connecting…',
  offline: 'Reconnecting…',
  retry: 'Try again',
  somethingWentWrong: 'Something went wrong',

  // Lobby
  lobbyTitle: 'Rounds',
  lobbyEmpty: 'No rounds open right now',
  lobbyEmptyHint: 'A new round opens shortly. Pull down to refresh.',
  entryFee: 'Entry',
  prize: 'Prize',
  players: (n: number) => (n === 1 ? '1 player' : `${n} players`),
  playersLabel: 'Players',
  waitingForPlayers: (have: number, need: number) => `${have} of ${need} players`,
  joinRound: 'Join',
  addCartela: 'Add cartela',
  leaveCartela: (secondsLeft: number) => `Leave (${secondsLeft}s)`,
  leaveCartelaOpen: 'Leave',
  roundFull: 'Room full',
  joined: 'Joined',
  watchRound: 'Watch',
  live: 'Live',
  startingSoon: 'Starting soon',
  /** Badge on the superadmin-only Arif Test round — no real money moves in it. */
  closesIn: (s: string) => `Closes in ${s}`,
  startsIn: (s: string) => `Starts in ${s}`,
  inDays: (n: number) => (n === 1 ? '1 day' : `${n} days`),
  weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  winBy: (what: string) => `Win by: ${what}`,
  patternSingleLine: 'a full row, column or diagonal',
  patternFourCorners: 'the four corners of your cartela',
  patternLineOrCorners: 'a full row, column, diagonal, or the four corners',
  patternFullHouse: 'marking all 24 numbers on your cartela',
  patternUnknown: (name: string) => `the “${name}” pattern`,

  // Balance
  balance: 'Balance',
  available: 'Available',
  withdrawable: 'Withdrawable',
  /** Migration 066: promotional money, playable but withheld from withdrawal. */
  bonusMoney: 'Bonus money',
  lockedInPlay: 'In play',

  // Buying
  cancel: 'Cancel',
  pickCartela: 'Tap a number to buy that cartela',

  // Live round
  lastBall: 'Last ball',
  called: (n: number) => `${n} called`,
  yourCard: 'Your card',
  cardOf: (i: number, total: number) => `Card ${i} of ${total}`,
  waitingToStart: 'Waiting for the round to start',
  firstBallSoon: 'First ball coming up',
  autoMark: 'Auto-mark',
  manualMark: 'Manual',
  gameInProgress: 'Game in progress — watch and wait for the next one',
  roundStarting: 'Starting…',

  // Outcomes
  youWon: 'You won!',
  youWonAmount: (amount: string) => `${amount} has been added to your balance.`,
  roundOver: 'Round over',
  someoneElseWon: 'Another player completed the pattern first.',
  /** The shared announcement every viewer sees — winner and everyone else alike. */
  winnerAnnouncement: (name: string, cartela: number, amount: string) =>
    `${name} (Cartela #${cartela}) won ${amount}`,
  /** §4 dual winner: the second line under the primary winner announcement. */
  splitPotWith: (name: string, cartela: number, amount: string) =>
    `Split with ${name} (Cartela #${cartela}), who also won ${amount}.`,
  yourCartelaNumber: (cartela: number) => `Cartela #${cartela}`,
  roundCancelled: 'Round cancelled',
  roundCancelledRefunded: 'Your entry fee has been refunded in full.',
  backToLobby: 'Back to rounds',
  /**
   * The outcome screen's next-round bar. `nextRoundJoinIn` counts down to the
   * moment joining closes, which is the deadline that actually costs a player
   * something to miss — not the start time.
   */
  nextRoundJoinIn: (s: string) => `Next round — joining closes in ${s}`,
  nextRoundOpening: 'Next round opening…',
  joinNextRound: 'Join',

  // Provable fairness
  fairness: 'Provably fair',
  fairnessCommitted: 'The draw order was locked in before the first ball.',
  fairnessCommitment: 'Commitment',
  fairnessSeed: 'Revealed seed',
  fairnessSeedHidden: 'Revealed when the round ends',
  fairnessExplain:
    'The server committed to a secret before drawing. When the round ends the secret is published, and anyone can re-derive every ball from it and check it against what was called.',

  // Errors, keyed to the server's stable codes
  errorInsufficientFunds: 'Not enough balance for this round',
  errorRoundFull: 'This round is full',
  errorCardLimit: 'You already hold the maximum number of cards for this round',
  errorRoundState: 'This round has already started',
  errorNotFound: 'Not found',
  errorRateLimited: 'Too many requests — please wait a moment',
  errorAccountNotActive: 'Your account is not active. Please contact support.',
  errorGeneric: 'Something went wrong. Please try again.',
  /** Rendered as the link text when a support contact is configured. */
  contactSupport: 'Contact support',

  /** Dismisses a confirmation. Not "back to rounds" — these screens
   *  return to the wallet, and a button that names the wrong destination is
   *  a small lie the user notices. */
  done: 'Done',

  // Navigation
  navRounds: 'Play',
  navWallet: 'Wallet',

  // Wallet
  walletTitle: 'Wallet',
  deposit: 'Deposit',
  withdraw: 'Withdraw',
  activity: 'Activity',
  noActivity: 'Nothing here yet',
  total: 'Total',

  // Wallet timeline entries that come from the ledger rather than a
  // deposit/withdrawal workflow row. The referral wording matches the bot's
  // push message for the same event on purpose — one event, one name.
  txReferralReward: 'Referral bonus',
  txWelcomeBonus: 'Welcome bonus',
  bonusLockedHint: 'Includes a play-through bonus — you can withdraw the rest. Bonuses leave your balance as you play.',
  txPrize: 'Prize won',
  txRefund: 'Refund',
  txCartelaPurchase: 'Cartela purchase',
  txCommission: 'Commission',
  txAdjustment: 'Adjustment',
  txOther: 'Account update',
  statusReversed: 'Reversed',

  // Invite a friend
  inviteTitle: 'Invite a friend',
  inviteBody: 'Share your link — when your friend makes their first deposit, you get 100 ETB.',
  inviteCopy: 'Copy link',
  inviteCopied: 'Copied!',

  // Past rounds
  pastRounds: 'Past rounds',
  pastRoundsEmpty: "You haven't joined a round yet",
  myCartelas: (numbers: string) => `Your cartela: ${numbers}`,
  winnerLine: (cartela: number, amount: string) =>
    `Won with cartela #${cartela} — ${amount}`,

  // Deposit
  depositTitle: 'Add money',
  depositStep1: 'Send the money',
  depositStep1Body: 'Transfer the amount you want to deposit to the account below.',
  depositStep2: 'Tell us about it',
  depositStep2Body:
    'Enter the amount you sent and the transaction reference from your receipt. We check it against our records and credit your balance.',
  depositAccountName: 'Account name',
  depositAccountNumber: 'Send to',
  copy: 'Copy',
  copied: 'Copied',
  depositMethod: 'Method',
  depositAmount: 'Amount you sent',
  depositReference: 'Transaction reference',
  depositReferenceHint: 'The transaction number on your TeleBirr receipt',
  depositSubmit: 'Submit for review',
  depositSubmitting: 'Submitting…',
  depositSubmitted: 'Submitted',
  depositSubmittedBody:
    'We will check your transfer and credit your balance. This usually takes a few minutes.',
  depositNoMethods: 'Deposits are unavailable right now',
  depositNoMethodsHint: 'No payment account is currently configured. Please try again later.',
  depositMinimum: (amount: string) => `Minimum ${amount}`,
  depositMaximum: (amount: string) => `Maximum ${amount}`,

  // Withdraw
  withdrawTitle: 'Withdraw',
  withdrawAvailable: (amount: string) => `${amount} available to withdraw`,
  withdrawAmount: 'Amount',
  withdrawDestination: 'Send to',
  withdrawDestinationHint: 'Your TeleBirr phone number',
  withdrawSubmit: 'Request withdrawal',
  withdrawSubmitting: 'Requesting…',
  withdrawMax: 'Max',
  withdrawRequested: 'Withdrawal requested',
  withdrawRequestedBody:
    'Your funds are on hold while we process the transfer. We aim to send it within 24 hours — you will be notified when it is on the way.',
  withdrawNothing: 'You have nothing available to withdraw yet',

  // Status labels
  statusSubmitted: 'Awaiting review',
  statusUnderReview: 'Under review',
  statusApproved: 'Credited',
  statusRejected: 'Rejected',
  statusRequested: 'Requested',
  statusProcessing: 'Sending',
  statusCompleted: 'Sent',
  statusFailed: 'Failed',
  // Split out from the generic failure so the one case a player can actually
  // do something about — retype the number — says so.
  statusWrongNumber: 'Wrong number',
  statusError: 'Error',

  // Form errors
  errorAmountRequired: 'Enter an amount',
  errorAmountInvalid: 'Enter an amount like 50.00',
  errorAmountTooSmall: (min: string) => `Minimum is ${min}`,
  errorAmountTooLarge: (max: string) => `Maximum is ${max}`,
  errorReferenceRequired: 'Enter the reference from your receipt',
  errorReferenceInvalid: 'The transaction number is 10 letters and digits, like DIA8M2ZPXQ',
  errorDestinationRequired: 'Enter where the money should be sent',
  // Says the shape, not just "invalid": a player who mistyped one digit needs
  // to know what a right answer looks like to find their own mistake.
  errorTelebirrNumber: 'Enter a 10-digit TeleBirr number starting with 09',
  errorDuplicateReference: 'That reference has already been submitted',
  errorDailyDepositCap: 'You have reached your deposit limit for today. Please try again tomorrow.',
  errorWithdrawalLimit:
    'You have reached your withdrawal limit for today. Please try again tomorrow.',
  errorMoreThanWithdrawable: 'More than your withdrawable balance',
  errorBonusNotWithdrawable:
    'Your balance includes bonus money that must be played, not withdrawn. Play it in a round — the rest is withdrawable.',

  // Language
  language: 'Language',

  // Terms & Conditions
  termsTitle: 'Terms & Conditions',
  termsBody:
    'By using Arif Bingo you agree to our Terms & Conditions and Privacy Policy. You must be at least 18 years old to play. Arif Bingo is not responsible for any financial losses incurred during gameplay.',
  termsAccept: 'I accept',

  // Data collection consent
  consentTitle: 'One more thing',
  consentBody:
    'We would like to collect your phone number and Telegram username to verify your identity and improve your experience. This information is kept private and is never shared with third parties.',
  consentAllow: 'Allow',
  consentSkip: 'Not now',
} as const;

/**
 * The shape every catalogue must satisfy. Derived from English, so translations
 * cannot drift out of sync without failing to compile.
 */
export type Messages = {
  [K in keyof typeof en]: (typeof en)[K] extends (...args: infer A) => string
    ? (...args: A) => string
    : (typeof en)[K] extends readonly string[]
      ? readonly string[]
      : string;
};

/**
 * Amharic.
 *
 * Numerals stay Western Arabic (1, 2, 3) rather than Ethiopic (፩, ፪, ፫): that
 * is what Ethiopian interfaces, keypads and money use in practice, and a bingo
 * card is the last place to be unconventional about digits.
 */
export const am: Messages = {
  appName: 'ቢንጎ',

  openInTelegram: 'ይህን መተግበሪያ ከቴሌግራም ይክፈቱ',
  openInTelegramHint: 'ማንነትዎን ለማረጋገጥ ቢንጎ በቴሌግራም ውስጥ ይሰራል።',
  connecting: 'በመገናኘት ላይ…',
  offline: 'እንደገና በመገናኘት ላይ…',
  retry: 'እንደገና ይሞክሩ',
  somethingWentWrong: 'የሆነ ችግር ተፈጥሯል',

  lobbyTitle: 'ዙሮች',
  lobbyEmpty: 'አሁን ክፍት ዙር የለም',
  lobbyEmptyHint: 'አዲስ ዙር በቅርቡ ይከፈታል። ለማደስ ወደታች ይጎትቱ።',
  entryFee: 'መግቢያ',
  prize: 'ሽልማት',
  players: (n: number) => (n === 1 ? '1 ተጫዋች' : `${n} ተጫዋቾች`),
  playersLabel: 'ተጫዋቾች',
  waitingForPlayers: (have: number, need: number) => `ከ${need} ተጫዋቾች ${have}`,
  joinRound: 'ይግቡ',
  addCartela: 'ካርቴላ ጨምር',
  leaveCartela: (secondsLeft: number) => `ውጣ (${secondsLeft}ሰ)`,
  leaveCartelaOpen: 'ውጣ',
  roundFull: 'ክፍሉ ሞልቷል',
  joined: 'ገብተዋል',
  watchRound: 'ይመልከቱ',
  live: 'በቀጥታ',
  startingSoon: 'በቅርቡ ይጀምራል',
  closesIn: (s: string) => `በ${s} ይዘጋል`,
  startsIn: (s: string) => `በ${s} ይጀምራል`,
  inDays: (n: number) => `${n} ቀን`,
  weekdays: ['እሑድ', 'ሰኞ', 'ማክሰኞ', 'ረቡዕ', 'ሐሙስ', 'አርብ', 'ቅዳሜ'],
  winBy: (what: string) => `የሚያሸንፉበት: ${what}`,
  patternSingleLine: 'ሙሉ ረድፍ፣ ዓምድ ወይም ሰያፍ',
  patternFourCorners: 'የካርቴላዎን አራት ማዕዘኖች',
  patternLineOrCorners: 'ሙሉ ረድፍ፣ ዓምድ፣ ሰያፍ ወይም የካርቴላዎን አራት ማዕዘኖች',
  patternFullHouse: 'በካርቴላዎ ላይ ያሉትን 24 ቁጥሮች በሙሉ ምልክት ማድረግ',
  patternUnknown: (name: string) => `የ“${name}” ዘዴ`,

  balance: 'ቀሪ ሂሳብ',
  available: 'ያለ',
  withdrawable: 'የሚወጣ',
  /** Migration 066: promotional money, playable but withheld from withdrawal. */
  bonusMoney: 'የጉርሻ ገንዘብ',
  lockedInPlay: 'በጨዋታ ላይ',

  cancel: 'ይቅር',
  pickCartela: 'ካርቴላ ለመግዛት ቁጥር ይንኩ',

  lastBall: 'የመጨረሻው ኳስ',
  called: (n: number) => `${n} ተጠርተዋል`,
  yourCard: 'የእርስዎ ካርድ',
  cardOf: (i: number, total: number) => `ካርድ ${i} ከ${total}`,
  waitingToStart: 'ዙሩ እስኪጀምር በመጠበቅ ላይ',
  firstBallSoon: 'የመጀመሪያው ኳስ በቅርቡ',
  autoMark: 'ራስ-ሰር ምልክት',
  manualMark: 'በእጅ',
  gameInProgress: 'ጨዋታ በመካሄድ ላይ ነው — ይመልከቱ እና የሚቀጥለውን ይጠብቁ',
  roundStarting: 'በመጀመር ላይ…',

  youWon: 'አሸነፉ!',
  youWonAmount: (amount: string) => `${amount} ወደ ሂሳብዎ ተጨምሯል።`,
  roundOver: 'ዙሩ አልቋል',
  someoneElseWon: 'ሌላ ተጫዋች ቀድሞ ጨርሷል።',
  winnerAnnouncement: (name: string, cartela: number, amount: string) =>
    `${name} (ካርቴላ #${cartela}) አሸነፈ/ች ${amount}`,
  splitPotWith: (name: string, cartela: number, amount: string) =>
    `ከ${name} (ካርቴላ #${cartela}) ጋር ተከፍሏል፣ እነሱም ${amount} አሸንፈዋል።`,
  yourCartelaNumber: (cartela: number) => `ካርቴላ #${cartela}`,
  roundCancelled: 'ዙሩ ተሰርዟል',
  roundCancelledRefunded: 'የመግቢያ ክፍያዎ ሙሉ በሙሉ ተመልሷል።',
  backToLobby: 'ወደ ዙሮች ተመለስ',
  nextRoundJoinIn: (s: string) => `ቀጣይ ዙር — ምዝገባ በ${s} ይዘጋል`,
  nextRoundOpening: 'ቀጣይ ዙር በመከፈት ላይ…',
  joinNextRound: 'ይግቡ',

  fairness: 'ፍትሃዊነቱ የሚረጋገጥ',
  fairnessCommitted: 'የኳሶቹ ቅደም ተከተል ከመጀመሪያው ኳስ በፊት ተቆልፏል።',
  fairnessCommitment: 'ቁርጠኝነት',
  fairnessSeed: 'የተገለጠ ዘር',
  fairnessSeedHidden: 'ዙሩ ሲያልቅ ይገለጣል',
  fairnessExplain:
    'ሰርቨሩ ኳሶቹን ከመሳቡ በፊት ለአንድ ሚስጥር ቁርጠኝነት አሳይቷል። ዙሩ ሲያልቅ ሚስጥሩ ይታተማል፣ ማንኛውም ሰው ሁሉንም ኳሶች ከዚያ አውጥቶ ከተጠሩት ጋር ማመሳከር ይችላል።',

  errorInsufficientFunds: 'ለዚህ ዙር በቂ ቀሪ ሂሳብ የለዎትም',
  errorRoundFull: 'ይህ ዙር ሞልቷል',
  errorCardLimit: 'ለዚህ ዙር የሚፈቀደውን ከፍተኛ የካርድ ብዛት ይዘዋል',
  errorRoundState: 'ይህ ዙር አስቀድሞ ተጀምሯል',
  errorNotFound: 'አልተገኘም',
  errorRateLimited: 'በጣም ብዙ ጥያቄዎች — እባክዎ ትንሽ ይጠብቁ',
  errorAccountNotActive: 'መለያዎ ንቁ አይደለም። እባክዎ ድጋፍን ያግኙ።',
  errorGeneric: 'የሆነ ችግር ተፈጥሯል። እባክዎ እንደገና ይሞክሩ።',
  contactSupport: 'ድጋፍን ያግኙ',

  done: 'ተጠናቋል',

  navRounds: 'ይጫወቱ',
  navWallet: 'ኪስ',

  walletTitle: 'ኪስ',
  deposit: 'ገንዘብ ያስገቡ',
  withdraw: 'ገንዘብ ያውጡ',
  activity: 'እንቅስቃሴ',
  noActivity: 'እስካሁን ምንም የለም',
  total: 'ጠቅላላ',

  txReferralReward: 'የግብዣ ጉርሻ',
  txWelcomeBonus: 'የመክፈቻ ጉርሻ',
  bonusLockedHint: 'የጉርሻ ገንዘብን ያካትታል — ቀሪውን ማውጣት ይችላሉ። ጉርሻ ሲጫወቱ ከሂሳብዎ ይወጣል።',
  txPrize: 'ሽልማት',
  txRefund: 'ተመላሽ',
  txCartelaPurchase: 'የካርቴላ ግዢ',
  txCommission: 'ኮሚሽን',
  txAdjustment: 'ማስተካከያ',
  txOther: 'የሂሳብ ማሻሻያ',
  statusReversed: 'ተመልሷል',

  inviteTitle: 'ጓደኛ ይጋብዙ',
  inviteBody: 'ማስፈንጠሪያዎን ያካፍሉ — ጓደኛዎ የመጀመሪያ ገቢውን ሲያደርግ፣ 100 ብር ያገኛሉ።',
  inviteCopy: 'ማስፈንጠሪያ ቅዳ',
  inviteCopied: 'ተቀድቷል!',

  pastRounds: 'ያለፉ ዙሮች',
  pastRoundsEmpty: 'እስካሁን ዙር አልተቀላቀሉም',
  myCartelas: (numbers: string) => `የእርስዎ ካርቴላ፦ ${numbers}`,
  winnerLine: (cartela: number, amount: string) =>
    `በካርቴላ #${cartela} አሸንፏል — ${amount}`,

  depositTitle: 'ገንዘብ ያክሉ',
  depositStep1: 'ገንዘቡን ይላኩ',
  depositStep1Body: 'ማስገባት የሚፈልጉትን መጠን ወደ ታች ወደተጠቀሰው ሂሳብ ያስተላልፉ።',
  depositStep2: 'ስለ እሱ ይንገሩን',
  depositStep2Body:
    'የላኩትን መጠን እና ከደረሰኝዎ ላይ ያለውን የግብይት ማጣቀሻ ያስገቡ። ከመዝገባችን ጋር አመሳክረን ቀሪ ሂሳብዎን እንሞላለን።',
  depositAccountName: 'የሂሳብ ስም',
  depositAccountNumber: 'ወደዚህ ይላኩ',
  copy: 'ቅዳ',
  copied: 'ተቀድቷል',
  depositMethod: 'ዘዴ',
  depositAmount: 'የላኩት መጠን',
  depositReference: 'የግብይት ማጣቀሻ',
  depositReferenceHint: 'በቴሌብር ደረሰኝዎ ላይ ያለው የግብይት ቁጥር',
  depositSubmit: 'ለግምገማ ያስገቡ',
  depositSubmitting: 'በማስገባት ላይ…',
  depositSubmitted: 'ገብቷል',
  depositSubmittedBody:
    'ዝውውርዎን አረጋግጠን ቀሪ ሂሳብዎን እንሞላለን። ይህ አብዛኛውን ጊዜ ጥቂት ደቂቃዎችን ይወስዳል።',
  depositNoMethods: 'አሁን ገንዘብ ማስገባት አይቻልም',
  depositNoMethodsHint: 'በአሁኑ ጊዜ የተዘጋጀ የክፍያ ሂሳብ የለም። እባክዎ ቆይተው ይሞክሩ።',
  depositMinimum: (amount: string) => `ዝቅተኛው ${amount}`,
  depositMaximum: (amount: string) => `ከፍተኛው ${amount}`,

  withdrawTitle: 'ገንዘብ ማውጣት',
  withdrawAvailable: (amount: string) => `${amount} ለማውጣት ዝግጁ ነው`,
  withdrawAmount: 'መጠን',
  withdrawDestination: 'ወደዚህ ይላክ',
  withdrawDestinationHint: 'የቴሌብር ስልክ ቁጥርዎ',
  withdrawSubmit: 'የማውጣት ጥያቄ ያቅርቡ',
  withdrawSubmitting: 'በመጠየቅ ላይ…',
  withdrawMax: 'ሙሉ',
  withdrawRequested: 'የማውጣት ጥያቄ ቀርቧል',
  withdrawRequestedBody:
    'ገንዘብዎ ተይዞ ይቆያል። ዝውውሩን በ24 ሰዓት ውስጥ ለማከናወን እንሞክራለን — ሲላክ ይነገርዎታል።',
  withdrawNothing: 'እስካሁን ሊወጣ የሚችል ገንዘብ የለዎትም',

  statusSubmitted: 'ግምገማ በመጠበቅ ላይ',
  statusUnderReview: 'በግምገማ ላይ',
  statusApproved: 'ገብቷል',
  statusRejected: 'ተቀባይነት አላገኘም',
  statusRequested: 'ተጠይቋል',
  statusProcessing: 'በመላክ ላይ',
  statusCompleted: 'ተልኳል',
  statusFailed: 'አልተሳካም',
  statusWrongNumber: 'የተሳሳተ ቁጥር',
  statusError: 'ስህተት',

  errorAmountRequired: 'መጠን ያስገቡ',
  errorAmountInvalid: 'እንደ 50.00 ያለ መጠን ያስገቡ',
  errorAmountTooSmall: (min: string) => `ዝቅተኛው ${min} ነው`,
  errorAmountTooLarge: (max: string) => `ከፍተኛው ${max} ነው`,
  errorReferenceRequired: 'ከደረሰኝዎ ላይ ያለውን ማጣቀሻ ያስገቡ',
  errorReferenceInvalid: 'የግብይት ቁጥሩ እንደ DIA8M2ZPXQ ያሉ 10 ፊደላትና ቁጥሮች ነው',
  errorDestinationRequired: 'ገንዘቡ የሚላክበትን ያስገቡ',
  errorTelebirrNumber: 'በ09 የሚጀምር የ10 አኃዝ የቴሌብር ቁጥር ያስገቡ',
  errorDuplicateReference: 'ያ ማጣቀሻ አስቀድሞ ገብቷል',
  errorDailyDepositCap: 'የዛሬውን የማስገቢያ ገደብ ደርሰዋል። እባክዎ ነገ ይሞክሩ።',
  errorWithdrawalLimit: 'የዛሬውን የማውጫ ገደብ ደርሰዋል። እባክዎ ነገ ይሞክሩ።',
  errorMoreThanWithdrawable: 'ከሚወጣው ቀሪ ሂሳብዎ በላይ ነው',
  errorBonusNotWithdrawable:
    'ቀሪ ሂሳብዎ ላይ የጉርሻ ገንዘብ አለ — ለማውጣት አይደለም፣ ለመጫወት ነው። በዙር ይጫወቱ፤ ቀሪው ይወጣል።',

  language: 'ቋንቋ',

  termsTitle: 'አስተዋይ ሁኔታ እና መመከያ',
  termsBody:
    'አሪፍ ቢንጎ በመጠቀም የአገልግሎት ውል እና የግላዊነት ፖሊሲያችንን ተስማምተዋል ማለት ነው። ለመጫወት ቢያንስ 18 ዓመት መሆን አለብዎት። አሪፍ ቢንጎ በጨዋታ ወቅት ለሚደርስ ማንኛውም የገንዘብ ኪሳራ ኃላፊነት አይወስድም።',
  termsAccept: 'ተስማማለሁ',

  consentTitle: 'መልመለስ ያስፈልጋል',
  consentBody:
    'ማንነትዎን ለማረጋገጥ እና ልምድዎን ለማሻሻል ስልክ ቁጥርዎን እና የቴሌግራም ተጠቃሚ ስምዎን መሰብሰብ እንፈልጋለን። ይህ መረጃ በሚስጥር ይያዛል እና ለሦስተኛ ወገን አይተላለፍም።',
  consentAllow: 'ፍቀድ',
  consentSkip: 'አሁን ሳይ',

};

/**
 * Afar, Tigrinya, Somali — locale slots wired up (selectable, persisted,
 * `lang` attribute set) but not yet translated. Deliberately English text,
 * not a placeholder like `[TODO: aa]`, so an untranslated locale still reads
 * as a working app rather than broken UI — but this is real money and legal
 * copy (T&C, consent, deposit/withdrawal), so replacing these with actual
 * `aa`/`ti`/`so` catalogues needs a native speaker, not a machine
 * translation, before any of these three is presented as available.
 */
export const aa: Messages = en;
export const ti: Messages = en;
export const so: Messages = en;

export const catalogues = { en: en as Messages, am, aa, ti, so } as const;
export type Locale = keyof typeof catalogues;
